import React, { useState } from "react";
import Button from "../../Elements/Button";

/**
 * Estado de la impresora en la cabecera de Barra.
 *
 * Solo se pinta donde hay Web Bluetooth — o sea, en el iPad de barra con Bluefy.
 * En Safari y en escritorio esto devuelve `null` y la pantalla queda exactamente
 * como estaba: ni un píxel de diferencia para Kong ni para el resto de clubes.
 *
 * El botón de conectar es grande a propósito. Web Bluetooth exige un gesto humano
 * para abrir el selector, así que ese toque es obligatorio al empezar el turno y
 * tiene que verse desde el otro lado de la barra, no ser un chip diminuto.
 */
export default function ImpresoraBarra({
  disponible, conectada, conectando, emparejada, papel, enCola, estacion,
  onConectar, onPrueba, onOlvidar,
}) {
  const [abierto, setAbierto] = useState(false);
  const [aviso, setAviso] = useState("");

  // Otro aparato tiene la impresora y está vivo: esta pantalla NO está averiada, está
  // mirando. Antes decía "sin impresora" en rojo y ofrecía conectar — que es justo lo
  // que no debe hacer, porque se la quitaría a la barra.
  const imprimeOtro = !conectada && Boolean(estacion?.activa);

  // Sin Web Bluetooth no hay impresora, y callarse del todo tiene un coste: quien está
  // montando el aparato de barra no distingue "este navegador no puede" de "esto no está
  // desplegado". Pero si HAY estación viva, eso es lo que importa y se dice: al camarero
  // no le sirve saber qué navegador hace falta, le sirve saber que el papel está saliendo.
  if (!disponible && !imprimeOtro) {
    return (
      <span data-testid="impresora-sin-soporte" className="text-xs text-[var(--muted-foreground)]">
        Para imprimir comandas, abre esta pantalla en Bluefy
      </span>
    );
  }

  const sinPapel = Boolean(papel?.sinPapel);
  const tapaAbierta = Boolean(papel?.tapaAbierta);
  const problema = conectada && (sinPapel || tapaAbierta);

  // Los tonos de aviso del sistema (tokens de toast): éxito / error / aviso.
  const tono = imprimeOtro
    ? "border-[var(--toast-success-border)] bg-[var(--toast-success-bg)] text-[var(--toast-success-text)]"
    : !conectada
    ? "border-[var(--toast-error-border)] bg-[var(--toast-error-bg)] text-[var(--toast-error-text)]"
    : problema
    ? "border-[var(--toast-warning-border)] bg-[var(--toast-warning-bg)] text-[var(--toast-warning-text)]"
    : "border-[var(--toast-success-border)] bg-[var(--toast-success-bg)] text-[var(--toast-success-text)]";

  // El texto habla de ESTE aparato, no de la impresora en abstracto: la conexión es de
  // cada dispositivo, y "Impresora desconectada" en una tablet se leía como "la impresora
  // está apagada" cuando en realidad la tenía otra. Un club con varias tablets se pisa.
  const texto = imprimeOtro
    ? `Imprimiendo en ${estacion.label}`
    : !conectada
    ? emparejada ? "Impresora no conectada aquí" : "Sin impresora en este aparato"
    : sinPapel ? "Sin papel"
    : tapaAbierta ? "Tapa abierta"
    : "Impresora lista";

  const conectar = async () => {
    setAviso("");
    const r = await onConectar();
    // Cerrar el selector es una decisión y no se regaña por ello. Cualquier otro fallo
    // SÍ se dice: quedarse mudo deja al del club sin nada que mirar ni que contar.
    if (!r?.ok && !r?.cancelado) {
      setAviso(
        `${r?.motivo || "No se pudo conectar"}. Comprueba que la impresora está encendida, ` +
        "que el Bluetooth y la ubicación del aparato están activados, y que no la tenga " +
        "cogida otro dispositivo."
      );
    }
  };

  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-1.5 ${tono}`}>
        <span data-testid="impresora-estado" className="text-sm font-semibold">
          🖨️ {texto}
        </span>

        {/* Tickets que no han salido: es de lo más importante que dice este chip, así
            que va en alto contraste y no en blanco translúcido sobre rosa claro. */}
        {enCola > 0 && (
          <span
            data-testid="impresora-cola"
            className="rounded-full bg-[var(--foreground)] px-2.5 py-0.5 text-xs font-bold text-[var(--background)]"
          >
            {enCola} sin imprimir
          </span>
        )}

        {/* Con otro aparato imprimiendo, el botón se esconde: conectar aquí le quitaría
            la impresora a la barra en mitad del servicio. Sigue en el desplegable de
            ajustes, con su aviso, para el relevo cuando de verdad haga falta. */}
        {!conectada && !imprimeOtro && (
          <Button size="sm" onClick={conectar} disabled={conectando}>
            {conectando ? "Conectando…" : emparejada ? "Conectar" : "Configurar"}
          </Button>
        )}

        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-label="Ajustes de impresora"
          className="px-1 text-sm opacity-60 hover:opacity-100"
        >
          ⚙
        </button>
      </div>

      {aviso && <span className="text-xs text-[var(--destructive)]">{aviso}</span>}

      {abierto && (
        <div className="flex flex-col items-end gap-2 rounded-md border border-[var(--border)] bg-[var(--card)] p-3 text-right">
          {emparejada && (
            <span className="text-xs text-[var(--muted-foreground)]">
              {emparejada.deviceName || "Impresora guardada"}
            </span>
          )}
          {imprimeOtro && disponible && (
            <Button variant="outline" size="sm" onClick={conectar} disabled={conectando}>
              Traer la impresora a este aparato
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onPrueba} disabled={!conectada}>
              Ticket de prueba
            </Button>
            {emparejada && (
              <Button variant="ghost" size="sm" className="!text-[var(--destructive)]" onClick={onOlvidar}>
                Olvidar
              </Button>
            )}
          </div>
          <span className="max-w-[16rem] text-[11px] leading-snug text-[var(--muted-foreground)]">
            Al abrir la barra hay que conectarla una vez. Después imprime sola cada comanda.
          </span>
          {/* El aviso que evita que alguien rompa el servicio sin querer: la impresora
              admite una sola conexión, y conectar aquí se la quita a quien la tenga. */}
          <span className="max-w-[16rem] text-[11px] leading-snug text-[var(--warning)]">
            Solo un aparato puede tener la impresora. Si la conectas aquí, se la quitas
            al que la esté usando.
          </span>
        </div>
      )}
    </div>
  );
}
